// Auditor portal shell (the read-only compliance view, Session K2). A minimal, branded frame
// around the reused compliance-reports hub: an ELE-logo header showing who is signed in and
// when access expires, a "Sign out" button, and a contact footer. NO office nav, sidebar or
// operational menus -- the auditor sees only the compliance reports. Signing out clears the
// session and returns to /auditor-login.
import { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ELE_LOGO_DATA_URI } from "../../lib/eleLogo";
import { ThemeToggle } from "../../components/ThemeToggle";
import { formatDateLongUtc } from "../../lib/format";

const PHONE = "0203 576 4760";
const EMAIL = "info@eastlondonelevators.co.uk";

function fmtExpiry(iso: string | null | undefined): string | null {
  if (!iso) return null;
  if (Number.isNaN(new Date(iso).getTime())) return null;
  // Format in UTC so the displayed date matches the canonical value the auditor was emailed
  // and the office sees (server auditorDateDisplay is UTC), never drifting by the viewer's
  // timezone.
  return formatDateLongUtc(iso);
}

export function AuditorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const org = user?.auditorOrg || "Auditor";
  const expires = fmtExpiry(user?.auditorExpiresAt ?? null);

  async function onLogout() {
    setBusy(true);
    try {
      await logout();
    } finally {
      // Always return to the auditor sign-in, even if the logout call hiccups (the cookie
      // is cleared server-side regardless and the route guard will require a fresh session).
      navigate("/auditor-login", { replace: true });
    }
  }

  return (
    // .auditor-ui scopes the Session AFK light/dark theme system to the auditor portal
    // (Session AOR): the shell defaults to light (index.html's pre-paint script), with a
    // toggle back to dark via ThemeToggle below, the same shared "ele-theme" preference every
    // other shell reads and writes.
    <div className="auditor-ui flex min-h-dvh flex-col bg-ele-base text-ele-body">
      <header className="border-b border-ele-purple-accent bg-ele-surface">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            {/* The brand mark goes home (Session AEA): /auditor redirects to the compliance
                reports hub, the auditor's one page. */}
            <Link
              to="/auditor"
              aria-label="Go to the compliance reports"
              className="flex flex-none items-center rounded-ele outline-none focus-visible:ring-2 focus-visible:ring-ele-purple"
            >
              <img src={ELE_LOGO_DATA_URI} alt="East London Elevators" className="h-10 w-auto" />
            </Link>
            <div>
              <p className="font-heading text-lg leading-tight tracking-wide text-ele-strong">Compliance Portal</p>
              <p className="font-menu text-xs uppercase tracking-widest text-ele-blue">East London Elevators Ltd</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-xs text-ele-body">
              <p>
                Logged in as <span className="text-ele-strong">{org}</span>
              </p>
              {expires && (
                <p>
                  Access expires: <span className="text-ele-strong">{expires}</span>
                </p>
              )}
            </div>
            <ThemeToggle />
            <button
              type="button"
              onClick={onLogout}
              disabled={busy}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs uppercase tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50"
            >
              {busy ? "Signing out..." : "Sign out"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="border-t border-ele-purple-accent px-4 py-4 text-center text-xs text-ele-body opacity-80">
        East London Elevators Ltd | {PHONE} | {EMAIL}
      </footer>
    </div>
  );
}
